// Pure logic for the kits catalogue's browse controls (filter-sort-design.md,
// section 3.3). No React, no fetch, no DOM: everything here is a plain
// function of its arguments, so it is unit-tested directly in
// kitsBrowse.test.js and trusted anywhere the catalogue page needs it.
//
// The filters object mirrors the URL/API param names verbatim (including the
// snake_case `half_double`) ON PURPOSE: design section 10.1 says "the fetch
// is `presetsApi.list({...filters, page, page_size: 48})` with no
// translation layer" -- spreading `filters` straight into both
// setSearchParams and the API call only works if the field names already
// match both places.
import { GENRES } from "../api/presets";

export const BPM_MIN = 40;
export const BPM_MAX = 300;
export const TOLERANCE_VALUES = [5, 10, 20];
export const DEFAULT_TOLERANCE = 5;
export const SORT_VALUES = ["newest", "downloads", "rating"];
export const DEFAULT_SORT = "newest";
// "" is the public catalogue; "favorites" is the signed-in viewer's own liked
// kits, served by GET /api/presets/favorites. It is a VIEW, not a filter: it
// changes which endpoint answers, so it never combines with genre/bpm/q/sort
// (that endpoint takes page and page_size and nothing else).
export const VIEW_VALUES = ["", "favorites"];
export const DEFAULT_VIEW = "";
export const MAX_QUERY_LENGTH = 80;

// Ratings shipped in Task 19, so this is on. Kept as a single flag (design
// section 12) so the "Best rated" segment and everything gated on it can be
// pulled with one flip if ratings ever need to come back off.
export const RATINGS_ENABLED = true;

export function defaultFilters() {
  return {
    genre: "",
    bpm: null,
    tolerance: DEFAULT_TOLERANCE,
    half_double: false,
    q: "",
    sort: DEFAULT_SORT,
    view: DEFAULT_VIEW,
  };
}

// searchParams -> filters. Never throws, never lets anything invalid reach
// the API (design section 10.1's "on invalid" column): an unrecognised genre
// is ignored (falls back to "All"), an out-of-range or non-integer bpm drops
// the target AND, with it, tolerance/half_double (they are meaningless
// without a target), an unrecognised tolerance falls back to 5, q is
// trimmed and truncated to 80 chars, and an unrecognised sort falls back to
// "newest".
export function parseKitsParams(searchParams) {
  const filters = defaultFilters();

  // Read first and return early: in the favourites view every other param is
  // a lie, because /api/presets/favorites cannot honour any of them. Returning
  // the defaults means the chips, the BPM box and the sort segments all render
  // in their neutral state instead of claiming a filter that is not applied.
  const view = searchParams.get("view") || "";
  if (VIEW_VALUES.includes(view) && view !== "") {
    filters.view = view;
    return filters;
  }

  const genre = searchParams.get("genre") || "";
  if (GENRES.includes(genre)) filters.genre = genre;

  const bpmRaw = searchParams.get("bpm");
  if (bpmRaw !== null && bpmRaw !== "") {
    const n = Number(bpmRaw);
    if (Number.isInteger(n) && n >= BPM_MIN && n <= BPM_MAX) {
      filters.bpm = n;
      const tol = Number(searchParams.get("tolerance"));
      filters.tolerance = TOLERANCE_VALUES.includes(tol) ? tol : DEFAULT_TOLERANCE;
      filters.half_double = searchParams.get("half_double") === "1";
    }
  }

  filters.q = (searchParams.get("q") || "").trim().slice(0, MAX_QUERY_LENGTH);

  const sort = searchParams.get("sort") || DEFAULT_SORT;
  filters.sort = SORT_VALUES.includes(sort) && (sort !== "rating" || RATINGS_ENABLED)
    ? sort
    : DEFAULT_SORT;

  return filters;
}

// filters -> URLSearchParams, omitting anything at its default so a clean
// visit is exactly `/kits` (design section 10.1).
export function serializeKitsParams(filters) {
  const params = new URLSearchParams();
  // Same reason as the parse side: the favourites URL carries view and nothing
  // else, so leaving the view never leaves a stale genre behind in the URL.
  if (filters.view === "favorites") {
    params.set("view", "favorites");
    return params;
  }
  if (filters.genre) params.set("genre", filters.genre);
  if (filters.bpm != null) {
    params.set("bpm", String(filters.bpm));
    if (filters.tolerance !== DEFAULT_TOLERANCE) {
      params.set("tolerance", String(filters.tolerance));
    }
    if (filters.half_double) params.set("half_double", "1");
  }
  if (filters.q) params.set("q", filters.q);
  if (filters.sort !== DEFAULT_SORT) params.set("sort", filters.sort);
  return params;
}

// Mirrors the backend's window math (design section 5.4 / 11) so the
// frontend can reason about the same windows the API will actually query,
// e.g. for a future facet strip. Bounds are inclusive; a window that falls
// entirely outside [BPM_MIN, BPM_MAX] is dropped.
export function bpmWindows(bpm, tolerance, halfDouble) {
  if (bpm == null) return [];
  const windows = [[bpm - tolerance, bpm + tolerance]];
  if (halfDouble) {
    windows.push([bpm / 2 - tolerance, bpm / 2 + tolerance]);
    windows.push([bpm * 2 - tolerance, bpm * 2 + tolerance]);
  }
  return windows.filter(([lo, hi]) => hi >= BPM_MIN && lo <= BPM_MAX);
}

export function isAnyFilterActive(filters) {
  return Boolean(filters.genre || filters.bpm != null || filters.q);
}

// The results-line copy (design section 8): count first always, then genre /
// bpm / search fragments in that order, joined by a middot. The component
// bolds the leading number itself (it's a display-only detail this pure string
// doesn't need to carry).
export function summarizeFilters(filters, total, shown) {
  const count = shown < total
    ? `Showing ${shown} of ${total} kits`
    : `${total} ${total === 1 ? "kit" : "kits"}`;

  const parts = [count];
  if (filters.genre) parts.push(filters.genre);
  if (filters.bpm != null) {
    let bpmPart = `around ${filters.bpm} BPM (±${filters.tolerance})`;
    if (filters.half_double) bpmPart += ", plus half/double time";
    parts.push(bpmPart);
  }
  if (filters.q) parts.push(`"${filters.q}"`);
  return parts.join(" · ");
}

// The "no results" body copy (design section 9.3): first rule that matches,
// in this priority order -- a bpm target always wins (it's the most specific
// signal), then genre-only, then search-only, then a generic fallback for
// any other combination.
export function noResultsBody(filters) {
  if (filters.bpm != null) {
    const lo = filters.bpm - filters.tolerance;
    const hi = filters.bpm + filters.tolerance;
    return `Nothing around ${filters.bpm} BPM (${lo} to ${hi}) yet.`;
  }
  if (filters.genre && !filters.q) return `No ${filters.genre} kits yet.`;
  if (filters.q && !filters.genre) return `No kits named like "${filters.q}".`;
  return "Try removing a filter.";
}

// The "no results" actions (design section 9.3): at most 3, in this order.
// `next` is a complete filters object ready to hand straight to
// serializeKitsParams -- the caller never has to know which fields changed.
export function emptyStateActions(filters) {
  const actions = [];

  if (filters.bpm != null && filters.tolerance < 20) {
    const nextTolerance = filters.tolerance < 10 ? 10 : 20;
    actions.push({
      label: `Widen to ±${nextTolerance} BPM`,
      next: { ...filters, tolerance: nextTolerance },
    });
  }
  if (filters.bpm != null) {
    actions.push({
      label: "Any tempo",
      next: { ...filters, bpm: null, tolerance: DEFAULT_TOLERANCE, half_double: false },
    });
  }
  if (filters.genre) {
    actions.push({ label: "All genres", next: { ...filters, genre: "" } });
  }
  if (filters.q) {
    actions.push({ label: "Clear search", next: { ...filters, q: "" } });
  }

  const activeCount =
    (filters.genre ? 1 : 0) + (filters.bpm != null ? 1 : 0) + (filters.q ? 1 : 0);
  if (activeCount >= 2) {
    actions.push({
      label: "Clear filters",
      next: {
        ...filters,
        genre: "",
        bpm: null,
        tolerance: DEFAULT_TOLERANCE,
        half_double: false,
        q: "",
      },
    });
  }

  // "At most three, in this order" (design section 9.3): truncate rather
  // than re-prioritise, so the order above is always the tie-break too.
  return actions.slice(0, 3);
}
